import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Floating "Zgłoś problem" button + modal. Renders globally for any logged-in user.
 * Captures: description, category, path, user_agent, viewport size, timestamp.
 * Backend stores in `user_feedback` table.
 */
export function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("bug");
  const [description, setDescription] = useState("");

  if (!user) return null;

  async function submit() {
    if (!description.trim() || description.trim().length < 5) {
      toast.error("Opisz problem (min. 5 znaków)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", {
        category,
        description: description.trim(),
        path: location.pathname + location.search,
        user_agent: navigator.userAgent.slice(0, 300),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      toast.success(`Dziękujemy! Zgłoszenie #${data.id?.slice(0, 8)} odebrane.`);
      setOpen(false);
      setDescription("");
      setCategory("bug");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wysłać zgłoszenia");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Position: bottom-left on mobile (below burger), bottom-left after sidebar (288px + 16px gap) on lg+.
          Rationale: keeps floating widget away from bottom-right where primary submit actions live
          (e.g. "Zapisz i wyślij" in daily reconciliation sticky footer). See UI audit 2026-07 P0-B1. */}
      <button onClick={() => setOpen(true)}
              data-testid="feedback-open-btn"
              className="fixed bottom-4 left-4 lg:left-[304px] z-40 inline-flex items-center gap-2 min-h-11 px-4 py-2 rounded-full
                         bg-[var(--tt-primary,#6b1c2c)] text-[var(--tt-primary-fg,#ffffff)] text-sm shadow-lg
                         hover:opacity-90 transition-opacity"
              title="Zgłoś problem lub pomysł">
        <MessageSquareWarning className="h-4 w-4" />
        <span className="hidden sm:inline">Zgłoś problem</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle>Zgłoś problem lub pomysł</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[var(--tt-muted)] bg-[var(--tt-surface-2,#f5efe5)] rounded p-2">
              Dziękujemy za pomoc w testach beta. Twoje zgłoszenie pomoże nam usprawnić aplikację.
            </div>
            <div>
              <Label className="text-xs">Typ zgłoszenia</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">🐛 Błąd / coś nie działa</SelectItem>
                  <SelectItem value="confusing">❓ Coś niezrozumiałego</SelectItem>
                  <SelectItem value="idea">💡 Pomysł / sugestia</SelectItem>
                  <SelectItem value="other">📝 Inne</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Opisz, co się stało (lub czego brakuje)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Np. Po kliknięciu 'Zapisz' nic się nie dzieje. Próbowałem dwa razy."
                rows={5}
                data-testid="feedback-description"
                maxLength={2000}
              />
              <div className="text-[10px] text-right text-[var(--tt-muted)]">
                {description.length} / 2000
              </div>
            </div>
            <div className="text-[10px] text-[var(--tt-muted)]">
              Wyślemy automatycznie: ekran ({location.pathname}), Twoją rolę, czas, przeglądarkę.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Anuluj</Button>
            <Button onClick={submit} disabled={busy} data-testid="feedback-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Wyślij zgłoszenie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
